import { get, isEmpty, merge, round } from 'lodash';
import * as d3 from 'd3';
import { Handle } from './vertical.slider.handle';
import { UserService } from '../../providers/user/user.service';
import {
  Component,
  OnInit,
  ElementRef,
  Input,
  Injectable,
  OnChanges,
  SimpleChanges,
  HostListener,
  Output,
  EventEmitter,
  DoCheck,
  AfterViewInit
} from '@angular/core';

interface IOptions {
  height: number;
  unit: string;
  precision: number;
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  inputWidth: number;
}

export interface IHandleOptions extends IHandleMinMax {
  precision: number;
  step: number;
  inputMargin: number;
  icon: string;
  callback: Function;
  padding: {
    left: number;
    width: number;
  };
  readOnly: boolean;
  color?: string;
}

interface IHandleMinMax {
  max: number;
  min: number;
}

interface ISettings extends IOptions {
  domain: Array<number>;
  readOnly: boolean;
  handleIcon: string;
  handles: {
    from: IHandleMinMax;
  };
}

interface ThresholdValues {
  valueFrom: number;
}

@Component({
  selector: 'vertical-single-slider',
  templateUrl: './vertical.slider.html',
  styleUrls: ['./vertical.slider.scss']
})
@Injectable()
export class VerticalSingleSliderComponent
  implements OnInit, OnChanges, DoCheck, AfterViewInit {
  elementRef: ElementRef;
  svgElement: any;
  svg: any = null;
  scale: any = null;
  slider: any = null;
  handles: any = {};
  prevClientWidth: number;
  color: string;

  defaultOptions: IOptions = {
    height: 200,
    unit: '',
    precision: 0,
    padding: {
      top: 12,
      right: 12,
      bottom: 12,
      left: 60
    },
    inputWidth: 80
  };

  @Output() setThreshold: EventEmitter<ThresholdValues> = new EventEmitter<
    ThresholdValues
  >();
  @Input() from: number;
  @Input() options: ISettings;

  constructor(elementRef: ElementRef, private userService: UserService) {
    this.elementRef = elementRef;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.redraw();
  }

  ngOnInit(): void {
    this.svgElement = this.elementRef.nativeElement.getElementsByTagName(
      'svg'
    )[0];
    this.color = this.getColorByTheme();
    this.draw();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.redraw();
    }, 0);
  }

  ngDoCheck() {
    // It will redraw the svg elements when width of the elements changes
    if (this.prevClientWidth !== this.width) {
      this.redraw();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.from !== get(changes, 'from.previousValue', this.from)) {
      if (this.from < this.settings.domain[1]) {
        this.from = this.settings.domain[1];
      }
      this.setThreshold.emit({ valueFrom: this.from });
    }

    if (!isEmpty(this.handles) && this.handles.from.value !== this.from) {
      this.updateHandlesValue();
    }
  }

  getColorByTheme(): string {
    if (this.userService.getThemeFromStorage() === 'dark') {
      return '#E5ECF5';
    } else {
      return '#000000';
    }
  }

  get step(): number {
    // minimum step value is 0.25
    const step = (this.settings.domain[0] - this.settings.domain[1]) / 40;
    return step <= 0.25 ? 0.25 : step;
  }

  get minStep(): number {
    return 1 / Math.pow(10, this.settings.precision);
  }

  updateHandlesValue(): void {
    const step = this.minStep;
    this.handles.from.value = this.from;
    this.handles.from.updateHandle(step, true);
  }

  get width(): number {
    return this.elementRef.nativeElement.clientWidth;
  }

  get settings(): ISettings {
    return merge({}, this.defaultOptions, this.options);
  }

  get range(): Array<number> {
    return [
      this.settings.padding.top + this.settings.padding.bottom,
      this.settings.height -
        this.settings.padding.top -
        this.settings.padding.bottom
    ];
  }

  get readOnly(): boolean {
    return this.settings.readOnly;
  }

  positiveNumbers($event): void {
    if ($event.charCode > 57 || $event.charCode < 46) {
      $event.preventDefault();
    }
  }

  draw(): void {
    this.prevClientWidth = this.width;
    this.svg = d3
      .select(this.svgElement)
      .attr('width', this.width)
      .attr('height', this.settings.height);

    this.scale = this.createScale();
    this.slider = this.createSlider();
    this.addYAxis();
    this.addXAxis();

    const handles = this.settings.handles;

    this.handles.from = new Handle(
      this.slider,
      this.scale,
      this.from,
      this.getHandleOptions(handles.from)
    );
    this.handles.from.updateHandle(this.minStep, false);
  }

  destroy(): void {
    const node = this.svg.node();
    while (node.firstChild) {
      node.removeChild(node.firstChild);
    }
  }

  redraw(): void {
    this.destroy();
    this.draw();
  }

  getHandleOptions(options: IHandleMinMax): IHandleOptions {
    const defaultOptions = {
      min: this.settings.domain[1] - this.step,
      max: this.settings.domain[0] + this.step,
      precision: this.settings.precision,
      step: this.step,
      inputMargin: 0,
      icon: this.settings.handleIcon,
      callback: this.updateLayout.bind(this),
      padding: {
        left: this.settings.padding.right,
        width:
          this.width - this.settings.padding.left - this.settings.inputWidth
      },
      readOnly: this.readOnly
    };

    return merge({}, defaultOptions, options);
  }

  updateLayout(): void {
    if (!this.handles.from) {
      return;
    }

    const valueFrom = this.handles.from.value;

    this.setThreshold.emit({
      valueFrom: valueFrom
    });
  }

  createScale(): any {
    const scale = d3.scaleLinear();
    scale.domain(this.settings.domain).range(this.range).clamp(true);

    return scale;
  }

  createSlider(): SVGGElement {
    return this.svg
      .append('g')
      .attr('class', 'slider')
      .attr(
        'transform',
        'translate(' + this.settings.padding.left + ',' + 0 + ')'
      );
  }

  addYAxis(): void {
    this.slider
      .append('line')
      .attr('class', 'track')
      .attr('stroke', this.color)
      .attr('y1', this.range[0])
      .attr('y2', this.range[1])
      .select(function () {
        return this.parentNode.appendChild(this.cloneNode(true));
      })
      .attr('class', 'track-inset')
      .select(function () {
        return this.parentNode.appendChild(this.cloneNode(true));
      })
      .attr('class', 'track-overlay');

    this.addYAxisLabels();
  }

  addYAxisLabels(): void {
    const textAnchor = 'text-anchor';
    const labelH = 3;
    let value = this.settings.domain[0];
    this.slider
      .append('text')
      .attr('class', 'labels')
      .attr('stroke', this.color)
      .attr('x', -this.settings.padding.right)
      .attr('y', this.scale(value) + labelH)
      .attr(textAnchor, 'end')
      .attr('font-size', '8pt')
      .text(value + this.settings.unit);

    value = round(this.settings.domain[0] / 2, this.settings.precision);
    this.slider
      .append('text')
      .attr('class', 'labels')
      .attr('stroke', this.color)
      .attr('x', -this.settings.padding.right)
      .attr('y', this.scale(value) + labelH)
      .attr(textAnchor, 'end')
      .attr('font-size', '8pt')
      .text(value + this.settings.unit);

    value = this.settings.domain[1];
    this.slider
      .append('text')
      .attr('class', 'labels')
      .attr('stroke', this.color)
      .attr('x', -this.settings.padding.right)
      .attr('y', this.scale(value) + labelH)
      .attr(textAnchor, 'end')
      .attr('font-size', '8pt')
      .text(value + this.settings.unit);
  }

  addXAxis(): void {
    this.slider
      .append('line')
      .attr('class', 'track')
      .attr('stroke', this.color)
      .attr('x1', 0)
      .attr(
        'x2',
        this.width - this.settings.padding.left - this.settings.inputWidth
      )
      .attr(
        'y1',
        this.settings.height -
          this.settings.padding.top -
          this.settings.padding.bottom
      )
      .attr(
        'y2',
        this.settings.height -
          this.settings.padding.top -
          this.settings.padding.bottom
      );
  }
}
